import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Icon } from "@iconify/react";

export default function AddMoreOnEvents({ open, onClose, eventId, categoryId }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const modalRef = useRef(null);

    const types = [
        { id: '1', name: 'announcement' },
        { id: '2', name: 'schedule' },
        { id: '3', name: 'speakers' },
        { id: '4', name: 'sponsors' },
        { id: '5', name: 'map' },
        { id: '6', name: 'exhibitors' },
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [open, onClose]);

    useEffect(() => {
        const fetchCategoryData = async () => {
            if (categoryId) {
                try {
                    const response = await axios.get(`${apiUrl}event-service/app-menu/${categoryId}`, {
                        headers: {
                            'Authorization': `Bearer ${bearerToken}`,
                        },
                    });

                    if (response.data && response.data.success) {
                        const data = response.data.data;
                        setInitialData({
                            name: data.name || '',
                            type: data.type || '',
                            icon: data.icon?.secure_url || '',
                        });
                        newCategory.setValues({
                            name: data.name || '',
                            type: data.type || '',
                            icon: data.icon?.secure_url || '',
                        });
                        setImagePreview(data.icon?.secure_url || '');
                    }
                } catch (error) {
                    console.error('Error fetching category data:', error.message);
                }
            }
        };

        if (open && categoryId) {
            fetchCategoryData();
        } else {
            newCategory.resetForm();
        }
    }, [open, categoryId]);

    const handleImagePreview = (event) => {
        const file = event.currentTarget.files[0];
        newCategory.setFieldValue('icon', file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleClose = () => {
        onClose();
        newCategory.resetForm();
        setImagePreview(null);
        setInitialData(null);
    };

    const addOrUpdateCategory = async (values) => {
        setIsLoading(true);
        try {
            const formValues = new FormData();
            formValues.append('name', values.name);
            formValues.append('eventId', eventId);
            formValues.append('type', values.type);
            formValues.append('icon', values.icon || '');

            const endpoint = categoryId
                ? `${apiUrl}event-service/app-menu/${categoryId}`
                : `${apiUrl}event-service/app-menu`;

            const method = categoryId ? 'put' : 'post';

            const response = await axios[method](endpoint, formValues, {
                headers: {
                    'Authorization': `Bearer ${bearerToken}`,
                    'Content-Type': 'multipart/form-data',
                    'accept': 'application/json',
                },
            });

            if (response.data && response.data.success) {
                console.log(`${categoryId ? 'Updated' : 'Created'} successfully:`, response.data);
            }
        } catch (error) {
            console.error('Error saving category:', error.message);
        } finally {
            setIsLoading(false);
            handleClose();
        }
    };

    const newCategory = useFormik({
        initialValues: {
            name: '',
            type: '',
            icon: '',
        },
        enableReinitialize: true, // Necessary to update form values on data load
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
            type: Yup.string().required('Required'),
            icon: Yup.mixed()
                .required('Required')
                .test('fileSize', 'File size is too large', (value) => {
                    if (typeof value === 'string') return true; // It's an existing image URL
                    return value && value.size <= 1 * 1024 * 1024;
                })
                .test('fileType', 'Unsupported file format', (value) => {
                    if (typeof value === 'string') return true; // Existing image URL
                    return value && ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
                }),

        }),
        onSubmit: async (values) => {
            await addOrUpdateCategory(values);
        }
    });

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black opacity-50" onClick={onClose}></div>

            {/* Modal */}
            <div className="bg-white rounded-lg w-full max-w-xl z-50 relative" ref={modalRef}>
                {/* Header */}
                <div className="bg-blue-500 text-white py-3 px-6 rounded-lg w-3/4 mx-auto relative z-10 -mt-6">
                    <h2 className="text-xl font-semibold text-center">
                        {categoryId ? 'Edit App Menu' : 'New App Menu'}
                    </h2>
                </div>

                <div className="relative bg-white p-6 space-y-6 shadow-md rounded-b-lg">
                    <form onSubmit={newCategory.handleSubmit} className="space-y-6">
                        <div className="flex space-x-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Section Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Name"
                                    value={newCategory.values.name}
                                    onChange={newCategory.handleChange}
                                    onBlur={newCategory.handleBlur}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {newCategory.touched.name && newCategory.errors.name && (
                                    <div className="text-red-500 text-xs mt-1">{newCategory.errors.name}</div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="type"
                                    value={newCategory.values.type}
                                    onChange={newCategory.handleChange}
                                    onBlur={newCategory.handleBlur}
                                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                >
                                    <option value="" disabled>
                                        Select Type
                                    </option>
                                    {types.map((category) => (
                                        <option key={category.name} value={category.name}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                                {newCategory.touched.type && newCategory.errors.type && (
                                    <div className="text-red-500 text-xs mt-1">{newCategory.errors.type}</div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center relative">
                                    {imagePreview ? (
                                        <>
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                className="w-full h-full object-cover rounded-lg"
                                            />
                                            {/* <button
                                                type="button"
                                                onClick={() => {
                                                    setImagePreview(null);
                                                    newCategory.setFieldValue('icon', null);
                                                    newCategory.setFieldTouched('icon', true);
                                                }}
                                                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center shadow-lg transition-colors"
                                                aria-label="Remove image"
                                            >
                                                <Icon icon="material-symbols-light:close-rounded" className="text-lg" />
                                            </button> */}
                                        </>
                                    ) : (
                                        <svg
                                            className="w-6 h-6 text-gray-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                    )}
                                </div>
                                <div className='bg-[#F8FCFF] p-2'>
                                    <p className="text-xs text-gray-400 mt-1 mb-1 italic">Recommended size: below 1mb, JPG/JPEG/PNG format</p>
                                    <label className="cursor-pointer ">
                                        <span className="text-blue-500 hover:text-blue-600 px-4 py-1 border border-blue-500 rounded-md text-sm ">
                                            {imagePreview ? 'Change Image' : 'Choose Image'}
                                        </span>
                                        <input
                                            type="file"
                                            name="icon"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => handleImagePreview(e, newCategory.setFieldValue)}
                                        />
                                    </label>
                                    {newCategory.touched.icon && newCategory.errors.icon && (
                                        <div className="text-red-500 text-xs mt-1">{newCategory.errors.icon}</div>
                                    )}
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {isLoading ? (categoryId ? 'Updating...' : 'Saving...') : (categoryId ? 'Update' : 'Save')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

AddMoreOnEvents.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    eventId: PropTypes.string.isRequired,
    categoryId: PropTypes.string,
};